/**
 * ═══════════════════════════════════════════════════════════════════════════
 * LAS OFERTAS DEL NEGOCIO — ESPEJO DE LA REGLA DEL SERVIDOR
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ⚠️ ESTE ARCHIVO NO SE EDITA A MANO.
 *
 * El original manda y vive en `functions/src/promocionesDeNegocio.ts` — ahí
 * está la historia completa: estas ofertas no descontaban nada, pero sí le
 * mandaban un push al cliente («¡Delivery GRATIS!») y sí le decían «ACTIVO» al
 * dueño. Y ahí está también por qué el envío entra en el bruto y los
 * descuentos no.
 *
 * Esta copia existe porque `functions/` y `src/` se compilan aparte y no se
 * pueden importar entre sí. Es el mismo caso que los bonos del chofer
 * (`constants/incentivos.ts` ↔ `functions/src/incentives.ts`), donde la
 * pantalla llegó a prometer RD$1,000 mientras el servidor pagaba RD$800.
 *
 * Todo lo que va debajo de la línea de marca se copia TAL CUAL del original, y
 * `scripts/probar-espejos.mjs` lo comprueba byte a byte antes de desplegar.
 * Para cambiar una regla: se cambia en el servidor y se vuelve a copiar.
 */

// @@REGLA-COMPARTIDA@@  de aquí abajo, idéntico en las dos copias.

/**
 * Lo que el negocio guarda en `restaurants/{id}.activePromotions`.
 *
 * `title` y `description` son lo que el cliente LEE (en el banner de Comida y
 * en la ficha del negocio); el resto son los números con los que se calcula.
 */
export type Oferta = {
  id: string;
  /**
   * `DELIVERY_GRATIS` o `DESCUENTO`.
   *
   * `VIERNES_LOCO` es LEGADO: es lo que escribía el panel viejo, y hay
   * negocios que ya lo tienen guardado. Se lee como un DESCUENTO del 50% solo
   * en el primer pedido, que es lo que su texto prometía. No se escribe más.
   */
  type: 'DELIVERY_GRATIS' | 'DESCUENTO' | 'VIERNES_LOCO';
  title: string;
  description: string;
  icon: string;
  color: string;
  /** Subtotal de comida mínimo para que la oferta aplique. 0 = sin mínimo. */
  minimo?: number;
  /** Para un DESCUENTO por porcentaje de la comida. */
  porcentaje?: number;
  /** Para un DESCUENTO de monto fijo en pesos. */
  monto?: number;
  /** Si solo aplica la primera vez que ese cliente le compra. */
  soloPrimerPedido?: boolean;
};

/**
 * Tope del porcentaje, y es TÉCNICO, no una opinión sobre su negocio.
 *
 * Con el 100%, el pedido sale en RD$0 y Stripe no puede cobrar cero: el
 * `PaymentIntent` falla y el cliente se queda sin poder pagar con tarjeta. El
 * negocio puede regalar lo que quiera por su cuenta; lo que no puede es dejar
 * un cobro en cero dentro de un cobrador que no acepta ceros.
 */
export const PORCENTAJE_MAXIMO = 90;

const num = (v: unknown, pordefecto = 0): number =>
  Number.isFinite(Number(v)) ? Number(v) : pordefecto;

/**
 * Deja una oferta guardada en forma utilizable, o `null` si no se puede usar.
 *
 * Devolver `null` en vez de arreglarla a medias es deliberado: una oferta sin
 * número no se puede calcular, y aplicar un 0% o un descuento de 0 pesos le
 * dejaría al cliente un banner que promete algo y una cuenta que no cambia.
 * Mejor que esa oferta no exista que que mienta.
 */
export function normalizarOferta(cruda: unknown): Oferta | null {
  const o = (cruda ?? {}) as Record<string, unknown>;
  const tipo = String(o.type ?? '').trim().toUpperCase();
  const base = {
    id: String(o.id ?? '').trim() || 'oferta',
    title: String(o.title ?? '').trim(),
    description: String(o.description ?? '').trim(),
    icon: String(o.icon ?? '').trim(),
    color: String(o.color ?? '').trim(),
    minimo: Math.max(0, Math.round(num(o.minimo))),
    soloPrimerPedido: o.soloPrimerPedido === true,
  };

  if (tipo === 'DELIVERY_GRATIS') {
    return { ...base, type: 'DELIVERY_GRATIS' };
  }

  // Legado: el panel viejo guardaba esto, con su texto prometiendo «mitad de
  // precio en el primer pedido». Se honra exactamente eso.
  if (tipo === 'VIERNES_LOCO') {
    return { ...base, type: 'DESCUENTO', porcentaje: 50, soloPrimerPedido: true };
  }

  if (tipo === 'DESCUENTO') {
    const pct = Math.round(num(o.porcentaje));
    if (pct > 0) {
      return { ...base, type: 'DESCUENTO', porcentaje: Math.min(PORCENTAJE_MAXIMO, pct) };
    }
    const monto = Math.round(num(o.monto));
    if (monto > 0) return { ...base, type: 'DESCUENTO', monto };
    return null; // un descuento sin cantidad no descuenta: no existe
  }

  return null;
}

/** Las ofertas utilizables de un negocio. */
export function ofertasDe(promos: unknown): Oferta[] {
  const lista = Array.isArray(promos) ? promos : [];
  return lista.map(normalizarOferta).filter((o): o is Oferta => o !== null);
}

/**
 * El envío que de verdad se cobra. `tarifaBase` es el `deliveryFee` del
 * negocio; la oferta solo puede bajarlo.
 */
export function envioConOfertas(
  tarifaBase: number,
  subtotalComida: number,
  promos: unknown
): { envio: number; envioGratisPorPromo: boolean } {
  const base = Math.max(0, num(tarifaBase));
  const sub = Math.max(0, num(subtotalComida));
  const gratis = ofertasDe(promos).some(
    (o) => o.type === 'DELIVERY_GRATIS' && sub >= (o.minimo ?? 0)
  );
  return { envio: gratis ? 0 : base, envioGratisPorPromo: gratis && base > 0 };
}

/**
 * El descuento en pesos sobre la comida, y con qué oferta.
 *
 * Si el negocio tiene varias, se aplica LA MEJOR PARA EL CLIENTE, no la suma.
 * Sumarlas podría dejar el pedido en cero sin que el dueño lo haya querido —
 * cada oferta la pensó por separado— y ninguna app las apila. Una sola, la que
 * más le conviene a quien está pagando.
 *
 * `esPrimerPedido` lo decide quien llama: el servidor consultando
 * `food_orders`, el carrito con la misma consulta cuando puede hacerla. Si no
 * se sabe, llega `false` y no se descuenta: nunca se adivina a favor.
 */
export function descuentoDeOfertas(
  subtotalComida: number,
  promos: unknown,
  esPrimerPedido: boolean
): { descuento: number; oferta: Oferta | null } {
  const sub = Math.max(0, num(subtotalComida));
  let mejor: Oferta | null = null;
  let mayor = 0;

  for (const o of ofertasDe(promos)) {
    if (o.type !== 'DESCUENTO') continue;
    if (sub < (o.minimo ?? 0)) continue;
    if (o.soloPrimerPedido && !esPrimerPedido) continue;

    const bruto = o.porcentaje
      ? Math.round((sub * o.porcentaje) / 100)
      : Math.round(o.monto ?? 0);
    // Nunca más que la comida: un monto fijo de 500 en un pedido de 300 no
    // puede ponerse a comerse el envío ni la propina del mensajero.
    const valor = Math.max(0, Math.min(sub, bruto));
    if (valor > mayor) {
      mayor = valor;
      mejor = o;
    }
  }

  return { descuento: mayor, oferta: mejor };
}

/**
 * Lo que el CLIENTE lee de la oferta, escrito con los mismos números con los
 * que se calcula.
 *
 * Esto no es cosmética: el panel viejo dejaba que el texto y la regla se
 * separaran, y decía «En pedidos mayores a RD$500» cuando en el código no
 * existía ningún mínimo. Generándolo de los números, no puede volver a
 * prometer una condición que no se aplica.
 */
export function resumenParaElCliente(o: Oferta): string {
  const desde = (o.minimo ?? 0) > 0 ? ` en pedidos de ${o.minimo} pesos o más` : '';
  const vez = o.soloPrimerPedido ? ' (solo en tu primer pedido aquí)' : '';
  if (o.type === 'DELIVERY_GRATIS') return `Delivery gratis${desde}${vez}.`;
  if (o.porcentaje) return `${o.porcentaje}% de descuento${desde}${vez}.`;
  return `${o.monto} pesos de descuento${desde}${vez}.`;
}

/** Lo que el DUEÑO lee: lo mismo, pero diciéndole que el costo lo asume él. */
export function resumenDeOferta(o: Oferta): string {
  const desde = (o.minimo ?? 0) > 0 ? ` en pedidos de ${o.minimo} pesos o más` : '';
  const vez = o.soloPrimerPedido ? ', solo la primera vez que cada cliente te compre' : '';
  if (o.type === 'DELIVERY_GRATIS') {
    return `El cliente no paga el delivery${desde}: lo asumes tú${vez}.`;
  }
  if (o.porcentaje) {
    return `${o.porcentaje}% de descuento en los productos${desde}${vez}. Ese descuento lo asumes tú.`;
  }
  return `${o.monto} pesos de descuento${desde}${vez}. Ese descuento lo asumes tú.`;
}
